import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { AnyAction } from "redux";
import { ThunkDispatch } from "redux-thunk";
import { AppState, AppTheme, ProfileReducerState, EventInterface, EventReducerState, EventsReducerState } from "../../../interfaces";
import { createStyles, makeStyles, Theme } from "@material-ui/core/styles";
import {
  Button,
  Grid,
  Container,
  Paper,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  InputAdornment
} from "@material-ui/core";
import { Search } from "@material-ui/icons";
import { DialogModal, ErrorView } from "../../../components";
import { clearEventError, getEvents, deleteEvent } from "../../../store/actions";
import { checkUserAccess } from "../../../util/permission-checker";
import PopupMenuDialog from "../../../components/PopupMenuDialog";
import UpsertEventDetailsModal from "./Modals/UpsertEventDetailsDialog";
import dateformat from "dateformat";

interface EventsProps {
  events: EventsReducerState;
  event: EventReducerState;
  profile: ProfileReducerState;
  appTheme: AppTheme;
  getEvents: (search: string, page: number, per_page: number) => void;
  clearEventError: () => void;
  deleteEvent: (_id: string) => void;
}

const useStyles = (appTheme: AppTheme) =>
  makeStyles((theme: Theme) =>
    createStyles({
      root: {
        flexGrow: 1,
        width: "100%"
      },
      paper: {
        backgroundColor: appTheme.section.backgroundColor,
        width: "100%",
        padding: 30,
        marginTop: 20,
        marginBottom: 20
      },
      icon: {
        color: theme.palette.text.primary,
        padding: 10,
        borderRadius: 30,
        backgroundColor: appTheme.iconBackgroundColor,
        "&:hover": {
          backgroundColor: appTheme.iconBackgroundHoverColor
        }
      },
      searchButton: {
        marginRight: "-21px",
        height: "55px",
        borderRadius: "30px",
        width: "100px"
      },
      textInput: {
        marginTop: "5px",
        width: "300px"
      },
      textInputControl: {
        borderRadius: "30px",
        backgroundColor: appTheme.section.backgroundColor
      },
      container: {
        minHeight: "60vh"
      },
      textInputIcon: {
        color: "#000"
      }
    })
  );

const Events: React.FC<EventsProps> = (props) => {
  const { appTheme, events, event, profile, getEvents, deleteEvent, clearEventError } = props;

  const [search, setSearch] = useState<string>(events.search);
  const [page, setPage] = useState<number>(events.page);
  const [perPage, setPerPage] = useState<number>(events.per_page ?? 20);
  const [selectedRecord, setSelectedRecord] = useState<EventInterface | null>(null);
  const [popUpDialogOpen, setPopUpDialogOpen] = useState<boolean>(false);

  const [upsertEventDetailsModelOpen, setUpsertEventDetailsModelOpen] = useState<boolean>(false);

  const popUpMenuItems = useMemo(() => {
    return [
      { text: "Update Event Details", permission: "edit-event" },
      { text: "Delete Event", permission: "delete-event" }
    ];
  }, []);

  const [menuItems, setMenuItems] = useState<Array<string>>(
    popUpMenuItems.filter((menuItem) => checkUserAccess(menuItem.permission, profile.permissions)).map((menuItem) => menuItem.text)
  );

  useEffect(() => {
    setMenuItems(popUpMenuItems.filter((menuItem) => checkUserAccess(menuItem.permission, profile.permissions)).map((menuItem) => menuItem.text));
  }, [popUpMenuItems, profile.permissions]);

  useEffect(() => {
    getEvents(search, page, perPage);
  }, [search, page, perPage, getEvents]);

  const styles = useStyles(appTheme)();

  type DialogOptions = {
    open: boolean;
    body: string;
  };

  const [dialogOptions, setDialogOptions] = useState<DialogOptions>({ open: false, body: "" });

  const dismissDialogModal = useCallback(() => {
    setDialogOptions({ open: false, body: "" });
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    return () => {
      clearEventError();
    };
  }, [clearEventError]);

  useEffect(() => {
    if (!event.error && event.response) {
      clearEventError();

      setSelectedRecord(null);

      setUpsertEventDetailsModelOpen(false);

      setDialogOptions({ open: true, body: event.response.data.status });

      getEvents("", 0, perPage);
    }
  }, [event.error, event.response, perPage, getEvents, clearEventError]);

  useEffect(() => {
    if (upsertEventDetailsModelOpen) {
      return;
    }

    const requestData = event.error ? event : null;

    if (!requestData) {
      return;
    }

    if (requestData.error && requestData.error.data && requestData.error.status) {
      let body: string = requestData.error.data.status;

      if (requestData.error.status === 500) {
        body = requestData.error.data.content;
      }

      setDialogOptions({ open: true, body: body });
    } else {
      if (requestData.error && requestData.error.message) {
        setDialogOptions({ open: true, body: requestData.error.message });
      }
    }
  }, [event, upsertEventDetailsModelOpen]);

  const onMenuItemSelected = (menuItem: string) => {
    setPopUpDialogOpen(false);

    if (!selectedRecord) {
      return;
    }

    switch (menuItem) {
      case "Update Event Details":
        setUpsertEventDetailsModelOpen(true);
        break;
      case "Delete Event":
        if (window.confirm("Would you like to delete this event ?")) {
          deleteEvent(selectedRecord.event_id);
        }
        break;
      default:
        break;
    }
  };

  return (
    <div className={styles.root}>
      <DialogModal open={dialogOptions.open} body={dialogOptions.body} onDismissed={dismissDialogModal} />
      <UpsertEventDetailsModal
        open={upsertEventDetailsModelOpen}
        onDismissed={() => setUpsertEventDetailsModelOpen(false)}
        eventData={selectedRecord}
      />
      <PopupMenuDialog
        open={popUpDialogOpen}
        onDismissed={() => setPopUpDialogOpen(false)}
        onMenuItemSelected={onMenuItemSelected}
        menuItems={menuItems}
      />
      <Container maxWidth="lg">
        {events.error ? (
          <ErrorView error={events.error} onRetry={() => getEvents(search, page, perPage)} />
        ) : (
          <React.Fragment>
            <Paper className={styles.paper}>
              <Grid container justifyContent="space-between" spacing={0}>
                <Grid item>
                  <Grid container spacing={1} alignItems="center">
                    <Grid item>
                      {(events.loading || event.loading) && <CircularProgress style={{ display: "block", margin: "auto" }} color="primary" />}
                    </Grid>
                    <Grid item>
                      <Typography variant="h3" color="primary">
                        Events
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item>
                  <TextField
                    onChange={(event) => {
                      setSearch(event.currentTarget.value);
                    }}
                    error={false}
                    value={search}
                    variant="outlined"
                    placeholder="Search ..."
                    className={styles.textInput}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search className={styles.textInputIcon} />
                        </InputAdornment>
                      ),
                      classes: { root: styles.textInputControl }
                    }}
                  />
                </Grid>
              </Grid>
              <br />
              <TableContainer className={styles.container}>
                <Table stickyHeader aria-label="events">
                  <TableHead>
                    <TableRow>
                      {[
                        { label: "ID", minWidth: 100 },
                        { label: "Name", minWidth: 150 },
                        { label: "Location", minWidth: 150 },
                        { label: "Description", minWidth: 400 },
                        { label: "Cover Image", minWidth: 100 },
                        { label: "Start Date & Time", minWidth: 200 },
                        { label: "End Date & Time", minWidth: 200 },
                        { label: "Options", minWidth: 100 }
                      ].map((column) => (
                        <TableCell key={column.label} align="center" style={{ minWidth: column.minWidth }}>
                          {column.label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {events.data.map((event: EventInterface) => {
                      const _id = event.event_id;
                      return (
                        <TableRow hover role="checkbox" tabIndex={-1} key={_id}>
                          {["event_id", "name", "location", "description", "cover_img", "start_time", "end_time", "options"].map((column) => {
                            const key = `${_id}-${column}`;

                            const data = (event as unknown as { [key: string]: any })[column];
                            if (column === "options") {
                              if (menuItems.length === 0) return <TableCell key={key} align="center"></TableCell>;
                              return (
                                <TableCell key={key} align="center">
                                  <Button
                                    variant="outlined"
                                    disabled={menuItems.length === 0}
                                    onClick={() => {
                                      setSelectedRecord(event);
                                      setPopUpDialogOpen(true);
                                    }}>
                                    Options
                                  </Button>
                                </TableCell>
                              );
                            } else if (column === "cover_img") {
                              return (
                                <TableCell key={key} align="center">
                                  <a href={data} target="_blank" rel="noreferrer" style={{ textDecoration: "none", color: "blue" }}>
                                    Click To View
                                  </a>
                                </TableCell>
                              );
                            } else if (column === "start_time" || column === "end_time") {
                              return (
                                <TableCell key={key} align="center">
                                  {dateformat(
                                    new Date(data).setHours(new Date(data).getHours() + new Date().getTimezoneOffset() / 60),
                                    "ddd mmm dd yyyy h:MM TT"
                                  )}
                                </TableCell>
                              );
                            } else {
                              return (
                                <TableCell key={key} align="center">
                                  {data}
                                </TableCell>
                              );
                            }
                          })}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <Grid container justifyContent="space-between" style={{ margin: "0px 10px 0px 10px" }}>
                <Grid item>
                  <Button
                    disabled={!checkUserAccess("add-event", profile.permissions)}
                    variant="outlined"
                    size="large"
                    onClick={() => {
                      setSelectedRecord(null);
                      setUpsertEventDetailsModelOpen(true);
                    }}>
                    Add New Event
                  </Button>
                </Grid>
                <Grid item>
                  <TablePagination
                    component="div"
                    count={events.total}
                    page={page}
                    onPageChange={(_, page) => setPage(page)}
                    rowsPerPage={perPage}
                    rowsPerPageOptions={[20, 50, 100, 1000]}
                    onRowsPerPageChange={(event) => {
                      setPerPage(parseInt(event.target.value));
                      setPage(0);
                    }}
                  />
                </Grid>
              </Grid>
            </Paper>
          </React.Fragment>
        )}
      </Container>
    </div>
  );
};

const mapDispatchToProps = (dispatch: ThunkDispatch<any, void, AnyAction>) => {
  return {
    getEvents: (search: string, page: number, per_page: number) => dispatch(getEvents(search, page, per_page)),
    clearEventError: () => dispatch(clearEventError()),
    deleteEvent: (_id: string) => dispatch(deleteEvent(_id))
  };
};

const mapStateToProps = (state: AppState) => {
  return {
    events: state.events,
    event: state.event,
    profile: state.profile,
    appTheme: state.general.theme
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Events);
